// Cadence › Meetings (`/c/{slug}/cadence/meeting`; 06 §Cadence "Weekly team meeting"): the
// recurring team meetings the caller attends or may see, each with its next and last meeting
// (a click opens the meeting screen) and its earlier meetings on request. Managers and a team
// lead's holder add a series (team, name, weekday and time → a weekly RRULE, attendee seats,
// facilitator), and edit or archive it from the row's hover icons.
import {
  Badge,
  Button,
  Checkbox,
  cn,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Field,
  FieldLabel,
  Input,
  LocationHeader,
  NativeSelect,
  NativeSelectOption,
  toast,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  ArchiveIcon,
  ArchiveRestoreIcon,
  CalendarClockIcon,
  ChevronRightIcon,
  PencilIcon,
  PlusIcon,
  StarIcon,
} from "lucide-react";
import { type FormEvent, useEffect, useId, useState } from "react";
import type { Api } from "../../api/client";
import type { MeetingSeriesView, MeetingSummary } from "../../api/types";
import { keepFocus } from "../../focus/focus";
import { orgChartQuery } from "../chart/chart-queries";
import { type CompanyAccess, isManager } from "../company";
import { Loading } from "../screen-parts";
import { problemText } from "../settings/problem-text";
import { ErrorNotice } from "../structure/parts";
import {
  describeSchedule,
  MEETING_STATE,
  meetingWhen,
  parseWeekly,
  WEEKDAYS,
  type Weekday,
  weeklyRule,
} from "./cadence-model";
import { HOVER_STRIP, IconAction } from "./cadence-parts";
import { cadenceKeys, seriesMeetingsQuery, seriesQuery, useSeatOptions } from "./cadence-queries";

export function MeetingsScreen({ api, company }: { api: Api; company: CompanyAccess }) {
  const co = company.id;
  const qc = useQueryClient();
  const list = useQuery(seriesQuery(api, co));
  const [dialog, setDialog] = useState<{ series: MeetingSeriesView | null } | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const archive = useMutation({
    mutationFn: (v: { series: MeetingSeriesView; archived: boolean }) =>
      api.client.meetingSeries.update({
        params: { co, id: v.series.id },
        body: { archived: v.archived },
      }),
    onSuccess: (_r, v) => {
      void qc.invalidateQueries({ queryKey: cadenceKeys.root(co) });
      toast.add({ title: v.archived ? "Meeting archived" : "Meeting restored", type: "success" });
    },
  });
  const data = list.data;
  const canCreate = (data?.teams ?? []).some((t) => t.can_create) || isManager(company);
  const live = (data?.data ?? []).filter((s) => !s.archived_at);
  const archived = (data?.data ?? []).filter((s) => s.archived_at);

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col" data-slot="meetings">
      <LocationHeader
        icon={CalendarClockIcon}
        title="Meetings"
        actions={
          canCreate ? (
            <Button size="sm" onClick={() => setDialog({ series: null })}>
              <PlusIcon data-icon="inline-start" aria-hidden="true" />
              <span className="max-sm:sr-only">Add meeting</span>
            </Button>
          ) : null
        }
      />
      <div className="min-h-0 flex-1 overflow-auto px-6 pt-4 pb-10 max-md:px-4">
        {list.isPending ? (
          <Loading label="Loading meetings" rows={4} />
        ) : list.isError ? (
          <ErrorNotice what="Meetings" error={list.error} />
        ) : !live.length ? (
          <EmptyState
            icon={CalendarClockIcon}
            className="mt-10"
            title="No team meeting yet"
            description="A weekly team meeting builds its agenda from the scorecard's misses, off-track priorities, the team's issues and the to-dos due, and posts its summary to the team's channel."
            actions={
              canCreate ? (
                <Button size="sm" onClick={() => setDialog({ series: null })}>
                  <PlusIcon data-icon="inline-start" aria-hidden="true" />
                  Add meeting
                </Button>
              ) : undefined
            }
          />
        ) : (
          <ul className="grid gap-3" data-slot="series-list">
            {live.map((s) => (
              <SeriesRow
                key={s.id}
                api={api}
                company={company}
                series={s}
                onEdit={() => setDialog({ series: s })}
                onArchive={() => archive.mutate({ series: s, archived: true })}
              />
            ))}
          </ul>
        )}
        {archived.length ? (
          <section className="mt-6">
            <button
              type="button"
              aria-expanded={showArchived}
              onClick={() => setShowArchived((o) => !o)}
              className="flex items-center gap-1.5 text-sm font-semibold text-foreground hover:underline"
            >
              <ChevronRightIcon
                aria-hidden="true"
                className={cn("size-4 transition-transform", showArchived && "rotate-90")}
              />
              Archived <span className="text-xs font-normal text-faint">{archived.length}</span>
            </button>
            {showArchived ? (
              <ul className="mt-2 divide-y divide-subtle rounded-md border border-subtle">
                {archived.map((s) => (
                  <li key={s.id} className="group/row flex h-10 items-center gap-3 px-3 text-sm">
                    <ArchiveIcon aria-hidden="true" className="size-4 text-faint" />
                    <span className="min-w-0 flex-1 truncate text-muted-foreground">{s.name}</span>
                    <span className={HOVER_STRIP}>
                      {s.can_edit ? (
                        <IconAction
                          label={`Restore ${s.name}`}
                          tip="Restore"
                          onClick={() => archive.mutate({ series: s, archived: false })}
                        >
                          <ArchiveRestoreIcon aria-hidden="true" />
                        </IconAction>
                      ) : null}
                    </span>
                  </li>
                ))}
              </ul>
            ) : null}
          </section>
        ) : null}
      </div>
      {dialog ? (
        <SeriesDialog
          api={api}
          company={company}
          series={dialog.series}
          teams={data?.teams ?? []}
          onClose={() => setDialog(null)}
        />
      ) : null}
    </div>
  );
}

function SeriesRow({
  api,
  company,
  series: s,
  onEdit,
  onArchive,
}: {
  api: Api;
  company: CompanyAccess;
  series: MeetingSeriesView;
  onEdit: () => void;
  onArchive: () => void;
}) {
  const [history, setHistory] = useState(false);
  const meetings = useQuery({ ...seriesMeetingsQuery(api, company.id, s.id), enabled: history });
  const shownIds = new Set([s.next_meeting?.id, s.last_meeting?.id]);
  return (
    <li
      className="group/row rounded-lg border border-border bg-card p-4"
      data-slot="series"
      aria-label={s.name}
    >
      <div className="flex min-w-0 items-start gap-3">
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-base font-semibold text-foreground">{s.name}</h2>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {s.team_name ?? "Company"} · {describeSchedule(s.schedule)} · {s.duration_minutes} min ·{" "}
            {s.attendees.length} {s.attendees.length === 1 ? "seat" : "seats"}
          </p>
        </div>
        <span className={HOVER_STRIP}>
          {s.can_edit ? (
            <>
              <IconAction label={`Edit ${s.name}`} tip="Edit" onClick={onEdit}>
                <PencilIcon aria-hidden="true" />
              </IconAction>
              <IconAction label={`Archive ${s.name}`} tip="Archive" onClick={onArchive}>
                <ArchiveIcon aria-hidden="true" />
              </IconAction>
            </>
          ) : null}
        </span>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3 max-md:grid-cols-1">
        <MeetingTile
          company={company}
          label="Next"
          meeting={s.next_meeting}
          empty="Nothing scheduled"
        />
        <MeetingTile
          company={company}
          label="Last"
          meeting={s.last_meeting}
          empty="No meeting held yet"
        />
      </div>
      <button
        type="button"
        aria-expanded={history}
        onClick={() => setHistory((h) => !h)}
        className="mt-3 flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
      >
        <ChevronRightIcon
          aria-hidden="true"
          className={cn("size-3.5 transition-transform", history && "rotate-90")}
        />
        All meetings
      </button>
      {history ? (
        meetings.isPending ? (
          <Loading label="Loading meetings" rows={2} />
        ) : (
          <ul className="mt-1 divide-y divide-subtle">
            {(meetings.data?.data ?? [])
              .filter((m) => !shownIds.has(m.id))
              .map((m) => (
                <li key={m.id}>
                  <MeetingLink company={company} meeting={m} />
                </li>
              ))}
            {(meetings.data?.data ?? []).every((m) => shownIds.has(m.id)) ? (
              <li className="py-1.5 text-xs text-faint">No other meetings.</li>
            ) : null}
          </ul>
        )
      ) : null}
    </li>
  );
}

function MeetingTile({
  company,
  label,
  meeting,
  empty,
}: {
  company: CompanyAccess;
  label: string;
  meeting: MeetingSummary | null;
  empty: string;
}) {
  return (
    <div
      className="rounded-md border border-subtle bg-sunken/50 px-3 py-2"
      data-slot={`meeting-${label.toLowerCase()}`}
    >
      <p className="text-2xs font-semibold tracking-wide text-faint uppercase">{label}</p>
      {meeting ? (
        <MeetingLink company={company} meeting={meeting} />
      ) : (
        <p className="py-1 text-sm text-muted-foreground">{empty}</p>
      )}
    </div>
  );
}

function MeetingLink({ company, meeting: m }: { company: CompanyAccess; meeting: MeetingSummary }) {
  const state = MEETING_STATE[m.state];
  return (
    <Link
      to="/c/$slug/kpis/meetings/$meetingId"
      params={{ slug: company.slug, meetingId: m.id }}
      search={keepFocus}
      className="flex min-w-0 items-center gap-2 py-1 text-sm text-foreground hover:underline"
    >
      <span className="truncate font-medium">
        {meetingWhen(m.scheduled_at, company.calendar?.timezone)}
      </span>
      <Badge variant={state.badge}>{state.label}</Badge>
      <span className="ml-auto flex shrink-0 items-center gap-2 text-xs text-muted-foreground tabular-nums">
        {m.rating ? (
          <span className="inline-flex items-center gap-0.5" title="Rating">
            <StarIcon aria-hidden="true" className="size-3" />
            {m.rating}/10
          </span>
        ) : null}
        <span>
          {m.items} {m.items === 1 ? "item" : "items"}
        </span>
        {m.outcomes ? (
          <span>
            {m.outcomes} {m.outcomes === 1 ? "outcome" : "outcomes"}
          </span>
        ) : null}
      </span>
    </Link>
  );
}

function SeriesDialog({
  api,
  company,
  series,
  teams,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  series: MeetingSeriesView | null;
  teams: { id: string; name: string; can_create: boolean }[];
  onClose: () => void;
}) {
  const co = company.id;
  const qc = useQueryClient();
  const seats = useSeatOptions(api, co);
  const id = useId();
  const manager = isManager(company);
  const creatable = teams.filter((t) => t.can_create);
  const slot = series ? parseWeekly(series.schedule) : null;
  const [team, setTeam] = useState(series ? (series.team_id ?? "") : (creatable[0]?.id ?? ""));
  const [name, setName] = useState(series?.name ?? "");
  const [weekday, setWeekday] = useState<Weekday>(slot?.weekday ?? "TU");
  const [time, setTime] = useState(
    slot
      ? `${String(slot.hour).padStart(2, "0")}:${String(slot.minute).padStart(2, "0")}`
      : "10:00",
  );
  const [duration, setDuration] = useState(String(series?.duration_minutes ?? 60));
  const [attendees, setAttendees] = useState<string[]>(series?.attendees ?? []);
  const [facilitator, setFacilitator] = useState(series?.facilitator_seat_id ?? "");
  // A new series for a team starts with the team's seats as attendees and its lead facilitating.
  const chartTeams = useQuery({ ...orgChartQuery(api, co), retry: false }).data;
  // biome-ignore lint/correctness/useExhaustiveDependencies: when the team changes
  useEffect(() => {
    if (series || !team || !chartTeams) return;
    const t = chartTeams.teams?.find((x) => x.id === team);
    const members = (chartTeams.team_members ?? [])
      .filter((m) => m.team_id === team)
      .map((m) => m.seat_id);
    setAttendees([...new Set([...(t?.lead_seat_id ? [t.lead_seat_id] : []), ...members])]);
    setFacilitator(t?.lead_seat_id ?? "");
    if (!name && t) setName(`${t.name} weekly`);
  }, [team, chartTeams]);

  const save = useMutation({
    mutationFn: () => {
      const [h, m] = time.split(":").map(Number) as [number, number];
      const body = {
        name: name.trim(),
        schedule: weeklyRule({ weekday, hour: h, minute: m }),
        duration_minutes: Number(duration) || 60,
        attendees,
        facilitator_seat_id: facilitator || null,
      };
      return series
        ? api.client.meetingSeries.update({ params: { co, id: series.id }, body })
        : api.client.meetingSeries.create({
            params: { co },
            body: { ...body, team_id: team || null },
          });
    },
    onSuccess: (s) => {
      void qc.invalidateQueries({ queryKey: cadenceKeys.root(co) });
      toast.add({
        title: series ? "Meeting saved" : "Meeting added",
        description: s.name,
        type: "success",
      });
      onClose();
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (name.trim() && /^\d{2}:\d{2}$/.test(time)) save.mutate();
  };
  return (
    <Dialog open onOpenChange={(o) => !o && !save.isPending && onClose()}>
      <DialogContent className="sm:max-w-[600px]">
        <form
          onSubmit={submit}
          aria-label={series ? "Edit meeting" : "Add meeting"}
          className="grid gap-5"
        >
          <DialogHeader>
            <DialogTitle>{series ? `Edit ${series.name}` : "Add a team meeting"}</DialogTitle>
            <DialogDescription>
              A weekly meeting in the company's time zone. Its agenda is built from the team's
              scorecard misses, priorities, issues and to-dos; its summary posts to the team's
              channel.
            </DialogDescription>
          </DialogHeader>
          {!series ? (
            <Field>
              <FieldLabel htmlFor={`${id}-team`}>Team</FieldLabel>
              <NativeSelect
                id={`${id}-team`}
                value={team}
                onChange={(e) => setTeam(e.target.value)}
                className="w-full"
              >
                {creatable.map((t) => (
                  <NativeSelectOption key={t.id} value={t.id}>
                    {t.name}
                  </NativeSelectOption>
                ))}
                {manager ? (
                  <NativeSelectOption value="">Company (no team)</NativeSelectOption>
                ) : null}
              </NativeSelect>
            </Field>
          ) : null}
          <Field>
            <FieldLabel htmlFor={`${id}-name`}>Name</FieldLabel>
            <Input
              id={`${id}-name`}
              value={name}
              required
              onChange={(e) => setName(e.target.value)}
            />
          </Field>
          <div className="grid grid-cols-3 gap-3 max-sm:grid-cols-1">
            <Field>
              <FieldLabel htmlFor={`${id}-day`}>Every</FieldLabel>
              <NativeSelect
                id={`${id}-day`}
                value={weekday}
                onChange={(e) => setWeekday(e.target.value as Weekday)}
                className="w-full"
              >
                {WEEKDAYS.map(([k, label]) => (
                  <NativeSelectOption key={k} value={k}>
                    {label}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            </Field>
            <Field>
              <FieldLabel htmlFor={`${id}-time`}>At</FieldLabel>
              <Input
                id={`${id}-time`}
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor={`${id}-len`}>Minutes</FieldLabel>
              <Input
                id={`${id}-len`}
                type="number"
                min={5}
                max={600}
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
              />
            </Field>
          </div>
          <Field>
            <FieldLabel htmlFor={`${id}-fac`}>Facilitator</FieldLabel>
            <NativeSelect
              id={`${id}-fac`}
              value={facilitator}
              onChange={(e) => setFacilitator(e.target.value)}
              className="w-full"
            >
              <NativeSelectOption value="">No facilitator</NativeSelectOption>
              {seats.map((s) => (
                <NativeSelectOption key={s.id} value={s.id}>
                  {s.label}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </Field>
          <fieldset className="grid gap-1.5">
            <legend className="mb-1.5 text-sm font-medium text-foreground">Attendees</legend>
            <div className="grid max-h-44 gap-1 overflow-auto rounded-md border border-subtle p-2">
              {seats.map((s) => {
                const on = attendees.includes(s.id);
                return (
                  // biome-ignore lint/a11y/noLabelWithoutControl: the checkbox is inside
                  <label key={s.id} className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={on}
                      onCheckedChange={(c) =>
                        setAttendees((a) => (c ? [...a, s.id] : a.filter((x) => x !== s.id)))
                      }
                    />
                    <span className="truncate">{s.label}</span>
                  </label>
                );
              })}
              {!seats.length ? (
                <p className="text-xs text-faint">No seats on the chart yet.</p>
              ) : null}
            </div>
          </fieldset>
          {save.isError ? (
            <p role="alert" className="text-sm text-danger-text">
              {problemText(save.error, "The meeting could not be saved.")}
            </p>
          ) : null}
          <DialogFooter>
            <DialogClose render={<Button type="button" variant="outline" />}>Cancel</DialogClose>
            <Button type="submit" disabled={save.isPending || !name.trim()}>
              {series ? "Save" : "Add meeting"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
